import { useEffect } from 'react';
import { mapsApi } from '@grandxl/api-client';
import { useLocationStore } from '../store/location.store';
// Reverse-geocode via the shared /maps proxy — the Google API key stays on
// the server. Returns city/state/display broken down for the location store.
//
// Precision priority (best → worst) for the display string:
//   1. `<street_number> <route>` — "35 Padre Pio Street"
//   2. `route` — "Padre Pio Street" (unmapped house numbers common in Nigeria)
//   3. `sublocality` / `neighborhood` — "Hange"
//   4. `city` — "Asuir" (the old fallback — least precise)
//
// Bolt / Uber show the street-level line by default; without it, riders can't
// find the right building. The `formatted_address` from Google is also stored
// as `display` when finer than our own assembly.
async function reverseGeocode(lat, lng) {
    try {
        const res = await mapsApi.reverse(lat, lng);
        const result = res.data.data.result;
        const parts = result?.address_components ?? [];
        if (parts.length === 0)
            return null;
        const get = (type) => parts.find((c) => c.types.includes(type))?.long_name ?? '';
        const streetNumber = get('street_number');
        const route = get('route');
        const sublocality = get('sublocality') || get('sublocality_level_1') || get('neighborhood');
        const city = get('locality') ||
            get('sublocality_level_1') ||
            get('administrative_area_level_2') ||
            'Your location';
        const state = get('administrative_area_level_1') || 'Benue';
        // Priority for the display string, precise → generic:
        //   1. `<street_number> <route>` — "35 Padre Pio Street" (best)
        //   2. `route` alone — Nigerian house numbers are often absent from Google's index
        //   3. Google's `formatted_address` first segment — the backend enriches this
        //      with the nearest POI name ("Snapple Hotel & Suites") when there's no
        //      street-level detail. This tier picks up that enrichment and is what
        //      makes the pin recognisable in informal-street areas.
        //   4. `sublocality` — "Hange" (rough neighbourhood — used when Google gave
        //      us NEITHER a route NOR a formatted_address the enrichment could
        //      improve; the display still needs *something*).
        //   5. `city` — last-resort fallback.
        let display;
        if (streetNumber && route) {
            display = `${streetNumber} ${route}, ${city}`;
        }
        else if (route) {
            display = `${route}, ${city}`;
        }
        else {
            const enriched = result?.formatted_address?.split(',')[0]?.trim() ?? '';
            display = enriched
                ? `${enriched}, ${city}`
                : (sublocality ? `${sublocality}, ${city}` : city);
        }
        return { city, state, display };
    }
    catch {
        return null;
    }
}
// Minimum time between background refreshes. Bolt/Uber pattern: refresh
// silently on every app open, but don't spam the API on client-side route
// changes within the same session. 60s balances "always fresh when they
// return" with "one refresh per meaningful session, not per navigation."
const REFRESH_DEBOUNCE_MS = 60 * 1000;
export function useDetectLocation() {
    const source = useLocationStore((s) => s.source);
    const lastRefreshedAt = useLocationStore((s) => s.lastRefreshedAt);
    const setLocation = useLocationStore((s) => s.setLocation);
    useEffect(() => {
        if (!navigator.geolocation)
            return;
        // If user has explicitly picked an address from the sheet (source='manual'),
        // don't overwrite it with a background GPS reading — that would silently
        // revert their choice.
        if (source === 'manual')
            return;
        // Rate-limit: skip if we've refreshed within the debounce window.
        if (lastRefreshedAt && Date.now() - lastRefreshedAt < REFRESH_DEBOUNCE_MS)
            return;
        // Only request GPS silently if permission is already granted. Anything
        // else (prompt/denied) waits for the user to tap "Use my current location"
        // explicitly — avoids a surprise permission popup on page load.
        void navigator.permissions.query({ name: 'geolocation' }).then((result) => {
            if (result.state !== 'granted')
                return;
            navigator.geolocation.getCurrentPosition(async (pos) => {
                const { latitude, longitude } = pos.coords;
                try {
                    const geo = await reverseGeocode(latitude, longitude);
                    if (geo) {
                        setLocation({ lat: latitude, lng: longitude }, geo.city, geo.display, 'gps', geo.state);
                    }
                    else {
                        setLocation({ lat: latitude, lng: longitude }, 'Your location', 'Location detected', 'gps', 'Benue');
                    }
                }
                catch {
                    setLocation({ lat: latitude, lng: longitude }, 'Your location', 'Location detected', 'gps', 'Benue');
                }
            }, () => {
                // Unavailable — silently do nothing, user can pick manually
            }, {
                // enableHighAccuracy: use GPS chip, not just cell-tower/wifi. Slower
                // (~2-4s) but essential for "you're AT the restaurant" precision.
                // maximumAge: 0 — refuse cached positions; the whole point of this
                // refresh is that the user has moved since we last checked.
                enableHighAccuracy: true,
                timeout: 12_000,
                maximumAge: 0,
            });
        }).catch(() => {
            // permissions.query not supported — skip silent auto-detect entirely
        });
    }, [source, lastRefreshedAt, setLocation]);
}
export { reverseGeocode };
//# sourceMappingURL=useDetectLocation.js.map